import { useEffect, useState } from "react"

import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@coval/ui"
import { Plus, Trash2, X } from "lucide-react"

import type { IVREdge, IVRMatchType, IVRNode } from "@/types/IVRFlow"

import type { NodeKind } from "./ivr-flow-tree-ops"
import { DEFAULT_MATCH_TYPE } from "./ivr-flow-utils"

const MATCH_TYPES: ReadonlyArray<{ value: IVRMatchType; label: string }> = [
  { value: "literal", label: "literal" },
  { value: "regex", label: "regex" },
  { value: "fuzzy", label: "fuzzy" },
]

export interface OutgoingEdgeEntry {
  edge: IVREdge
  index: number
}

interface NodeEditorProps {
  node: IVRNode
  isStart: boolean
  kind: NodeKind
  allNodeIds: string[]
  outgoingEdges: OutgoingEdgeEntry[]
  onUpdateNode: (patch: Partial<IVRNode>) => void
  onRenameNode: (nextId: string) => boolean
  onSetKind: (kind: NodeKind) => void
  onDelete: () => void
  onSetStart: () => void
  onAddEdge: () => void
  onUpdateEdge: (index: number, patch: Partial<IVREdge>) => void
  onRemoveEdge: (index: number) => void
}

export const NodeEditor = ({
  node,
  isStart,
  kind,
  allNodeIds,
  outgoingEdges,
  onUpdateNode,
  onRenameNode,
  onSetKind,
  onDelete,
  onSetStart,
  onAddEdge,
  onUpdateEdge,
  onRemoveEdge,
}: NodeEditorProps) => {
  const otherNodeIds = allNodeIds.filter((id) => id !== node.id)

  // Local draft so partial typing doesn't trigger a rename on every keystroke
  // (which would also break other references mid-edit). Commit on blur/Enter.
  const [idDraft, setIdDraft] = useState(node.id)
  useEffect(() => {
    setIdDraft(node.id)
  }, [node.id])

  const commitIdDraft = () => {
    const trimmed = idDraft.trim()
    if (!trimmed || trimmed === node.id) {
      setIdDraft(node.id)
      return
    }
    const ok = onRenameNode(trimmed)
    if (!ok) {
      setIdDraft(node.id)
    }
  }

  return (
    <div className="rounded-md border border-coval-border bg-surface-0 p-3 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-[10px] font-mono uppercase tracking-wide text-coval-text-muted shrink-0">
            id
          </span>
          <Input
            aria-label={`Node id for ${node.id}`}
            value={idDraft}
            onChange={(e) => setIdDraft(e.target.value)}
            onBlur={commitIdDraft}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault()
                e.currentTarget.blur()
              } else if (e.key === "Escape") {
                setIdDraft(node.id)
                e.currentTarget.blur()
              }
            }}
            className="font-mono text-xs h-7 flex-1 min-w-0"
          />
          {isStart && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-coval-info-surface text-coval-info-on-surface font-medium shrink-0">
              start
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!isStart && (
            <Button type="button" variant="ghost" onClick={onSetStart}>
              Set as start
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            icon
            tooltip={`Delete node ${node.id}`}
            onClick={onDelete}
          >
            <Trash2 className="w-4 h-4 text-coval-danger-text" />
          </Button>
        </div>
      </div>

      <div className="space-y-1">
        <Label className="text-xs">Prompt</Label>
        <Textarea
          aria-label={`Prompt for node ${node.id}`}
          value={node.prompt}
          rows={2}
          onChange={(e) => onUpdateNode({ prompt: e.target.value })}
          placeholder="e.g. Thanks for calling. Press 1 for sales."
        />
      </div>

      <div className="flex gap-1">
        {(
          [
            { value: "auto_advance", label: "Auto advance" },
            { value: "edges", label: "Edges" },
            { value: "terminal", label: "Terminal" },
          ] as const
        ).map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onSetKind(opt.value)}
            className={`px-2 py-1 text-xs rounded-sm border transition-colors ${
              kind === opt.value
                ? "border-coval-border-active bg-surface-1 text-coval-text"
                : "border-coval-border bg-surface-0 text-coval-text-muted hover:bg-surface-1-hover"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {kind === "auto_advance" && (
        <div className="space-y-1">
          <Label className="text-xs">Advance to</Label>
          <Select
            value={node.auto_advance_to ?? ""}
            onValueChange={(value) => onUpdateNode({ auto_advance_to: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select target node" />
            </SelectTrigger>
            <SelectContent>
              {otherNodeIds.map((id) => (
                <SelectItem key={id} value={id}>
                  {id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {kind === "edges" && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs">
              Outgoing edges{" "}
              <span className="text-[10px] text-coval-text-muted font-normal">
                (first match wins)
              </span>
            </Label>
            <Button type="button" variant="ghost" onClick={onAddEdge}>
              <Plus className="w-3 h-3 mr-1" />
              Add edge
            </Button>
          </div>
          {outgoingEdges.length === 0 && (
            <p className="text-xs text-coval-text-muted">No edges yet.</p>
          )}
          {outgoingEdges.map(({ edge, index }) => {
            const matchType = edge.match_type ?? DEFAULT_MATCH_TYPE
            return (
              <div
                key={index}
                className="space-y-1.5 rounded-sm border border-coval-border bg-surface-1/30 p-2"
              >
                <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                  <Input
                    aria-label="Edge input"
                    placeholder={
                      matchType === "regex"
                        ? "\\d{4}"
                        : matchType === "fuzzy"
                          ? "operator"
                          : "1"
                    }
                    value={edge.input}
                    onChange={(e) =>
                      onUpdateEdge(index, { input: e.target.value })
                    }
                  />
                  <Select
                    value={edge.destination}
                    onValueChange={(value) =>
                      onUpdateEdge(index, { destination: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Destination" />
                    </SelectTrigger>
                    <SelectContent>
                      {allNodeIds.map((id) => (
                        <SelectItem key={id} value={id}>
                          {id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="ghost"
                    icon
                    tooltip="Remove edge"
                    onClick={() => onRemoveEdge(index)}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-coval-text-muted shrink-0 mr-1">
                    match
                  </span>
                  {MATCH_TYPES.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() =>
                        onUpdateEdge(index, {
                          match_type:
                            opt.value === DEFAULT_MATCH_TYPE
                              ? undefined
                              : opt.value,
                        })
                      }
                      className={`px-1.5 py-0.5 text-[10px] rounded-sm border transition-colors ${
                        matchType === opt.value
                          ? "border-coval-border-active bg-surface-1 text-coval-text"
                          : "border-coval-border bg-surface-0 text-coval-text-muted hover:bg-surface-1-hover"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
